import {
  GasDatepicker,
  GasPickerDay,
  NextMonthDate,
  PreviousMonthDate,
  MONTH,
  DAYS,
} from './gas-datepicker.interface';
import { BehaviorSubject } from 'rxjs';

const DATE_SETTING = {
  minIndexDay: 1,
  weekLast: 7,
  minLength: 42,
  minMonthIndex: 0,
  maxMonthIndex: 11,
};

// @dynamic
/** deprecated */
export class GasDatepickerHelper {

  public actualDate$: BehaviorSubject<Date> = new BehaviorSubject(null);

  public months = MONTH;
  public days = DAYS;

  /**
   * Возвращает массив с днями недели
   */
  public getDays(): string[] {
    return this.days;
  }

  /**
   * Возвращает массив с месяцами
   */
  public getMonths(): string[] {
    return this.months;
  }

  /**
   * Создание объекта с корректными значениями для заполнения календаря
   *
   * @param month - текущий выбранный месяц
   * @param year - текущий выбранный год
   * @param day - текущий выбранный день
   */
  public createCurrentDateObject(day: Date = new Date(), month: number = day.getMonth(), year: number = day.getFullYear()): GasDatepicker {
    return {
      currentDay: day,
      currentMonth: month,
      currentYear: year,
      lastDay: this.getLastDay(month, year),
      indexDay: this.getIndexFirstDay(month, year),
    };
  }

  /** Устанавливает последний день текущего месяца */
  public getLastDay(month: number, year: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  /** Устанавливает индекс первого дня текущего месяца (Пн, Вт....Вс) */
  public getIndexFirstDay(month: number, year: number): number {
    return new Date(year, month, 1).getDay();
  }

  /** Формирует массив для заполнения datepickera */
  public setDaysInMonth(dateSetting: GasDatepicker, minDate?: Date, maxDate?: Date): GasPickerDay[] {
    const dateObject = this.currentMonthDays(dateSetting, minDate, maxDate);
    dateObject.unshift(...this.previusMonthDays(dateSetting, minDate, maxDate));
    dateObject.push(...this.nextMonthDays(dateObject, dateSetting, minDate, maxDate));
    return dateObject;
  }

  /**
   * Заполняет массив днями текущего месяца
   */
  public currentMonthDays(dateSetting: GasDatepicker, minDate?: Date, maxDate?: Date): GasPickerDay[] {
    const daysInMonth: GasPickerDay[] = [];
    for (let i = 1; i <= dateSetting.lastDay; i++) {
      const currentDate = new Date(dateSetting.currentYear, dateSetting.currentMonth, i);
      daysInMonth.push({
        value: new Date(dateSetting.currentYear, dateSetting.currentMonth, i),
        isActive: this.isActiveDay(currentDate, dateSetting) ? true : false,
        otherMonth: false,
        isCurrent: this.checkCurrendDay(currentDate, minDate, maxDate),
      });
    }

    return daysInMonth;
  }

  /**
   * Добавляет в начало массива дни, если месяц начинается не с понедельника
   *
   * @param dateSetting - объект с текущими корректнми значениями даты (понедельник = 1)
   */
  public previusMonthDays(dateSetting: GasDatepicker, minDate?: Date, maxDate?: Date): GasPickerDay[] {
    const daysPreviusMonth: GasPickerDay[] = [];
    if (dateSetting.indexDay !== DATE_SETTING.minIndexDay) {
      const previusMonthDate: PreviousMonthDate = this.getPreviousMonthDate(dateSetting);

      for (let i = 0; i < previusMonthDate.currectIndex - 1; i++) {
        let day = previusMonthDate.previusMonthLastDay - i;
        const currentDate = new Date(dateSetting.currentYear, previusMonthDate.previusMonth, day);

        day++;
        daysPreviusMonth.unshift({
          value: currentDate,
          otherMonth: true,
          isActive: false,
          monthIndex: -1,
          isCurrent: this.checkCurrendDay(currentDate, minDate, maxDate),
        });
      }
    }

    return daysPreviusMonth;
  }

  /**
   * Добавляет в конец массива дни, если в pickere осталось свободное место
   * (на экране всегда должно показываться 35 дней - 5 рядов*7дней)
   */
  public nextMonthDays(daysInMonth: GasPickerDay[], dateSetting: GasDatepicker, minDate: Date, maxDate: Date): GasPickerDay[] {
    const daysNextMonth: GasPickerDay[] = [];
    const nextMonthDate: NextMonthDate = this.getNextMonthDate(dateSetting);

    let iterator = 0;
    let i = daysInMonth.length;
    while (i < DATE_SETTING.minLength) {
      const day = nextMonthDate.previusMonthLastDay + iterator;
      const currentDate = new Date(dateSetting.currentYear, nextMonthDate.nextMonth, day);
      daysNextMonth.push({
        value: currentDate,
        otherMonth: true,
        isActive: false,
        monthIndex: 1,
        isCurrent: this.checkCurrendDay(currentDate, minDate, maxDate),
      });
      iterator++;
      i++;
    }

    return daysNextMonth;
  }

  /**
   * Проверяет выбранный день, что бы выделить его в календаре
   * сравнение строго на день, месяц и год
   */
  public isActiveDay(date: Date, dateSetting: GasDatepicker): boolean {
    return date.getDate() === dateSetting.currentDay.getDate() &&
      dateSetting.currentMonth === dateSetting.currentDay.getMonth() &&
      dateSetting.currentYear === dateSetting.currentDay.getFullYear();
  }

  /** Возвращает объект с данными о предыдущем месяце */
  public getPreviousMonthDate(dateSetting: GasDatepicker): PreviousMonthDate {
    const previusMonth = dateSetting.currentMonth - DATE_SETTING.minIndexDay < DATE_SETTING.minMonthIndex ?
      DATE_SETTING.maxMonthIndex : dateSetting.currentMonth - DATE_SETTING.minIndexDay;
    const previusMonthLastDay = new Date(dateSetting.currentYear, previusMonth + 1, 0).getDate();
    const currectIndex = dateSetting.indexDay < DATE_SETTING.minIndexDay ? DATE_SETTING.weekLast : dateSetting.indexDay;

    return {
      previusMonth,
      previusMonthLastDay,
      currectIndex,
    };
  }

  /** Возвращает объект с данными о следующем месяце */
  public getNextMonthDate(dateSetting: GasDatepicker): NextMonthDate {
    const nextMonth = dateSetting.currentMonth + 1 > 11 ? 0 : dateSetting.currentMonth + 1;
    const previusMonthLastDay = new Date(dateSetting.currentYear, nextMonth + 1, 1).getDate();

    return {
      nextMonth,
      previusMonthLastDay,
    };
  }

  /** Проверка на равенство выбранного дня текущему */
  public checkToDay(selectDay: Date): boolean {
    const currentDay = new Date();

    return selectDay.getDate() === currentDay.getDate()
      && selectDay.getMonth() === currentDay.getMonth()
      && selectDay.getFullYear() === currentDay.getFullYear();
  }

  public checkCurrendDay(currentDate?: Date, minDate?: Date, maxDate?: Date): boolean {
    currentDate?.setHours(0, 0, 0, 0);
    minDate?.setHours(0, 0, 0, 0);
    maxDate?.setHours(0, 0, 0, 0);

    if (currentDate?.getTime() < minDate?.getTime() || currentDate?.getTime() > maxDate?.getTime()) {
      return false;
    }

    return true;
  }
}
